import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CheckIcon, MinusIcon } from 'assets/icons';
import { generalApi, GeneralApiTagTypes } from 'core/api';
import { useGetAttachmentQuery, useUpdateAttachmentMutation } from 'core/attachments/api';
import { useAppDispatch } from 'hooks';
import { TPillSelectOptionBase } from 'types/ui';
import { booleanToString, stringToBoolean } from 'utils/booleanParser';

export const useAttachmentSentStatus = (attachmentId: number) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [inputValue, setInputValue] = useState<string | undefined>(undefined);

    const [updateAttachment] = useUpdateAttachmentMutation();
    const { data: attachment, isLoading } = useGetAttachmentQuery({ attachmentId });

    const statusOptions = useMemo<TPillSelectOptionBase[]>(
        () => [
            {
                value: 'false',
                label: t('attachments.mail.notSent'),
                icon: <MinusIcon />,
                backgroundColor: 'var(--color-gray-100)',
                color: 'var(--color-gray-800)',
            },
            {
                value: 'true',
                label: t('attachments.mail.sent'),
                icon: <CheckIcon />,
                backgroundColor: 'var(--color-green-100)',
                color: 'var(--color-green-700)',
            },
        ],
        [t],
    );

    const handleStatusChange = async (value: string) => {
        if (!attachment) return;

        try {
            await updateAttachment({
                attachmentId,
                sent: stringToBoolean(value),
            }).unwrap();
            dispatch(generalApi.util.invalidateTags([{ type: GeneralApiTagTypes.invoice.list }]));
        } catch (error: any) {
            if (error?.data?.message) {
                return toast.error(error.data.message);
            }
        }
    };

    useEffect(() => {
        if (!attachment) return;
        setInputValue(booleanToString(attachment.sent));
    }, [attachment]);

    return {
        inputValue,
        attachment,
        setInputValue,
        statusOptions,
        handleStatusChange,
        isLoading,
    };
};
